import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  effect,
  forwardRef,
  model,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { TranslateLangPipe, IconComponent } from '@gs-2.0/shared';

const MULTI_SELECT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectFieldComponent),
  multi: true,
};

const MULTI_SELECT_VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => MultiSelectFieldComponent),
  multi: true,
};

@Component({
  selector: 'app-multi-select-field',
  standalone: true,
  imports: [NgClass, MultiSelectModule, FormsModule, TranslateLangPipe, IconComponent],
  providers: [MULTI_SELECT_VALUE_ACCESSOR, MULTI_SELECT_VALIDATOR],
  templateUrl: './multi-select-field.component.html',
  styleUrl: './multi-select-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectFieldComponent<T = any> implements ControlValueAccessor, Validator {
  // Core inputs
  label = input<string>('');
  description = input<string>('');
  placeholder = input<string>('');
  errorMessage = input<string>('');
  chipsLabel = input<string>('Selected:');
  selectedItemsLabel = input<string>('{0} items selected');
  required = input<boolean>(false);
  disabled = signal(false);
  options = input<T[]>([]);
  optionLabel = input<string>('name');
  optionValue = input<string | undefined>(undefined);

  // Display & behavior
  filter = input<boolean>(false);
  filterPlaceholder = input<string>('');
  ariaLabel = input<string>('Select');
  validationDelay = input<number>(300);

  // Outputs
  change = output<T[]>();
  blur = output<void>();
  focus = output<void>();

  // Internal state
  value = model<T[] | null>(null);
  isTouched = signal(false);
  showDelayedError = signal(false);
  private validationTimer: any = null;

  // Computed
  selectedOptions = computed(() => {
    const optVal = this.optionValue();
    const current = this.value();
    if (!optVal || !current || (current as any[]).length === 0) return [];
    return this.options().filter((opt) => (current as any[]).includes((opt as any)[optVal]));
  });

  isInvalid = computed(
    () =>
      this.required() &&
      (!this.value() || (this.value() as any[]).length === 0) &&
      this.isTouched() &&
      this.showDelayedError(),
  );

  effectiveAriaLabel = computed(() => this.ariaLabel() || this.label());

  // ControlValueAccessor callbacks
  private onChangeCallback: (value: T[] | null) => void = () => {};
  private onTouchedCallback: () => void = () => {};

  constructor() {
    effect(() => {
      const currentValue = this.value();
      queueMicrotask(() => {
        this.onChangeCallback(currentValue);
      });
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: T[] | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: T[] | null) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    if (this.required() && (!control.value || control.value.length === 0)) {
      return { required: true };
    }
    return null;
  }

  // Event handlers
  onBlur(): void {
    this.isTouched.set(true);
    this.onTouchedCallback();
    this.blur.emit();

    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    this.validationTimer = setTimeout(() => {
      this.showDelayedError.set(true);
    }, this.validationDelay());
  }

  onFocus(): void {
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    this.showDelayedError.set(false);
    this.focus.emit();
  }

  onChange(event: any): void {
    this.value.set(event.value);
    this.showDelayedError.set(false);
    this.change.emit(event.value);
  }

  removeItem(option: T): void {
    const optVal = this.optionValue();
    if (!optVal) return;
    const itemValue = (option as any)[optVal];
    const updated = ((this.value() ?? []) as any[]).filter((v) => v !== itemValue);
    this.value.set(updated as T[]);
    this.showDelayedError.set(false);
    this.change.emit(updated as T[]);
  }
}
